import {
  AppShell,
  Button,
  Group,
  Loader,
  Stack,
  TextInput,
  Switch,
  Paper,
  Fieldset,
  Modal,
  Select,
  Text,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import {
  reset,
  initialize,
  updateField,
} from "../slices/form/emailTemplateTab";
import { reset as emailTemplateSchedulerReset } from "../slices/form/emailTemplateScheduler";
import {
  EmailGuestsRequest,
  SaveEmailTemplateRequest,
  useEmailGuestsMutation,
  useGetEmailTemplateQuery,
  useSaveEmailTemplateMutation,
} from "../slices/api/emailApi";
import { useGetVentQuery } from "../slices/api/ventsApi";
import { useEffect, useState } from "react";
import {
  displayErrorMessage,
  displayInfoMessage,
  displayServerErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import { IsBlockNav, SetBlockNav } from "../App";
import { Editor } from "../shared/components/Editor";
import { useLocation } from "wouter";
import { LabelWithTooltipHelp } from "../shared/components/LabelWithTooltipHelp";
import { TabHeader } from "../shared/components/TabHeader";
import EmailTemplateScheduler from "./EmailTemplateScheduler";
import { useDisclosure } from "@mantine/hooks";
import ShowMacros from "./ShowMacros";
import {
  RecipientFilterType,
  recipientFilterTypeToText,
  textToRecipientFilterType,
} from "../slices/types";
import { LinkAnchorToGuests } from "../shared/components/LinkAnchors";

interface Props {
  emailTemplateId: string;
  ventId: string;
  setTabDescription: (name: string) => void;
  setEventTabDescription: (name: string) => void;
}

const EmailTemplateTab = (props: Props) => {
  const ventId = props.ventId;
  const [_] = useLocation();
  const { data: stateData, hasChanges } = useAppSelector(
    (state: RootState) => state.emailTemplateTab,
  );
  const [opened, { open, close }] = useDisclosure(false);
  const [editorKey, setEditorKey] = useState(0); // Key to force re-render of Editor
  const {
    data: emailTemplateResponse,
    isFetching: isFetchingEmailTemplateResponse,
    isError: isErrorFetchingEmailTemplateResponse,
    error: emailTemplateError,
  } = useGetEmailTemplateQuery({ id: props.emailTemplateId });
  const {
    data: ventResponse,
    isError: isErrorFetchingVent,
    error: ventError,
  } = useGetVentQuery({
    id: ventId,
  });
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (emailTemplateResponse) {
      dispatch(initialize(emailTemplateResponse));
      props.setTabDescription(emailTemplateResponse.name);
      if (ventResponse) {
        props.setEventTabDescription(ventResponse.name);
      }
    }
  }, [emailTemplateResponse]);
  useEffect(() => {
    // During development useEffect is called twice by React.
    // We need to protect against that by returning if hasChanges didn't change
    if (IsBlockNav() === hasChanges) return;
    if (hasChanges) {
      SetBlockNav(true);
    } else {
      // Can happen if we support undo
      SetBlockNav(false);
    }
  }, [hasChanges]);

  const [saveEmailTemplate, { isLoading: isSavingEmailTemplate }] =
    useSaveEmailTemplateMutation();

  const [oneTimeRecipientFilterType, setOneTimeRecipientFilterType] =
    useState<RecipientFilterType>("all");
  const [emailGuests, { isLoading: isSending }] = useEmailGuestsMutation();

  const handleSave = async () => {
    if (stateData == null || emailTemplateResponse == null) {
      console.error("undefined");
      return;
    }
    if (stateData.name == null) {
      displayErrorMessage("Name is required.");
      return;
    }

    const request: SaveEmailTemplateRequest = {
      id: stateData.id!,
      name: stateData.name,
      body: stateData.body,
      bodyHtml: stateData.bodyHtml,
      textMessageBody: stateData.textMessageBody,
      subject: stateData.subject,
      includeGuests: stateData.includeGuests,
      excludeDeclinedGuests: stateData.excludeDeclinedGuests,
      excludeUndecidedGuests: stateData.excludeUndecidedGuests,
      includeActivities: stateData.includeActivities,
      replyToEmail: stateData.replyToEmail,
      replyToName: stateData.replyToName,
      emailRemindersRelativeTo: stateData.emailRemindersRelativeTo,
      emailReminders: stateData.emailReminders,
      updateTime: emailTemplateResponse.updateTime,
    };

    try {
      await saveEmailTemplate(request).unwrap();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  const handleSendEmail = async () => {
    try {
      const request: EmailGuestsRequest = {
        emailTemplateId: props.emailTemplateId,
        isOneTime: true,
        oneTimeRecipientFilterType: oneTimeRecipientFilterType,
      };
      await emailGuests(request).unwrap();
      displayInfoMessage("Emails sent successfully...");
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (isErrorFetchingEmailTemplateResponse || isErrorFetchingVent) {
    handleFetchingError(emailTemplateError ?? ventError);
    return null;
  }
  if (
    isFetchingEmailTemplateResponse ||
    !emailTemplateResponse ||
    !stateData ||
    !ventResponse
  ) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }
  return (
    <>
      <Modal
        size="xl"
        opened={opened}
        onClose={close}
        withCloseButton={false}
        centered
      >
        <ShowMacros ventId={props.ventId} onClose={close} />
      </Modal>
      <AppShell.Main>
        <TabHeader
          label="Email Template for"
          ventId={ventId}
          ventName={ventResponse.name}
        />
        <Stack gap="xs">
          <TextInput
            disabled={!ventResponse.canUserEdit}
            maxLength={100}
            label="Template name"
            style={{ width: "300px" }}
            required
            value={stateData?.name ?? ""}
            onChange={(event) =>
              dispatch(
                updateField({
                  field: "name",
                  value: event.currentTarget.value,
                }),
              )
            }
          />
          <Group>
            <TextInput
              disabled={!ventResponse.canUserEdit}
              maxLength={50}
              label={
                <LabelWithTooltipHelp
                  helpText="If specified, then the guest will be able to reply to the email."
                  labelText="Reply to email"
                />
              }
              style={{ width: "200px" }}
              value={stateData?.replyToEmail ?? ""}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "replyToEmail",
                    value: event.currentTarget.value,
                  }),
                )
              }
            />
            <TextInput
              disabled={!ventResponse.canUserEdit}
              maxLength={50}
              label="Reply to name"
              style={{ width: "200px" }}
              value={stateData?.replyToName ?? ""}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "replyToName",
                    value: event.currentTarget.value,
                  }),
                )
              }
            />
          </Group>
          <TextInput
            disabled={!ventResponse.canUserEdit}
            maxLength={250}
            label="Subject"
            style={{ width: "300px" }}
            required
            value={stateData?.subject ?? ""}
            onChange={(event) =>
              dispatch(
                updateField({
                  field: "subject",
                  value: event.currentTarget.value,
                }),
              )
            }
          />
        </Stack>
        <Paper mt="sm">
          <Editor
            key={editorKey} // Use key to force re-render when reset
            label="Body"
            required
            disabled={!ventResponse.canUserEdit}
            maxLength={10000}
            value={stateData.bodyHtml ?? ""}
            onChange={(htmlContent, textContent) => {
              dispatch(
                updateField({
                  field: "bodyHtml",
                  value: htmlContent,
                }),
              );
              dispatch(
                updateField({
                  field: "body",
                  value: textContent,
                }),
              );
            }}
          ></Editor>
        </Paper>
        <Button onClick={open} mt="md">
          Macros
        </Button>
        <Fieldset legend="Guest view options">
          <Switch
            disabled={!ventResponse.canUserEdit}
            label="Show guests in email"
            mt="md"
            checked={stateData.includeGuests}
            onChange={(event) =>
              dispatch(
                updateField({
                  field: "includeGuests",
                  value: event.currentTarget.checked,
                }),
              )
            }
          />
          <Fieldset legend="Exceptions">
            <Switch
              disabled={!ventResponse.canUserEdit}
              label="Exclude undecided guests"
              mt="md"
              checked={stateData.excludeUndecidedGuests}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "excludeUndecidedGuests",
                    value: event.currentTarget.checked,
                  }),
                )
              }
            />
            <Switch
              disabled={!ventResponse.canUserEdit}
              label="Exclude declined guests"
              mt="md"
              checked={stateData.excludeDeclinedGuests}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "excludeDeclinedGuests",
                    value: event.currentTarget.checked,
                  }),
                )
              }
            />
          </Fieldset>
          <Switch
            disabled={!ventResponse.canUserEdit}
            label="Show activities in email"
            mt="md"
            checked={stateData.includeActivities}
            onChange={(event) =>
              dispatch(
                updateField({
                  field: "includeActivities",
                  value: event.currentTarget.checked,
                }),
              )
            }
          />
        </Fieldset>
        <Fieldset
          legend="Automation"
          disabled={!ventResponse.canUserEdit}
          mt="md"
        >
          <EmailTemplateScheduler
            emailTemplateId={emailTemplateResponse.id}
            emailRemindersRelativeTo={
              emailTemplateResponse.emailRemindersRelativeTo
            }
            emailReminders={emailTemplateResponse.emailReminders}
            onChange={(emailRemindersRelativeTo, emailReminders) => {
              dispatch(
                updateField({
                  field: "emailRemindersRelativeTo",
                  value: emailRemindersRelativeTo,
                }),
              );
              dispatch(
                updateField({
                  field: "emailReminders",
                  value: emailReminders,
                }),
              );
            }}
          />
        </Fieldset>
        <div>
          <Button
            mt="xs"
            mr="md"
            onClick={() => {
              dispatch(emailTemplateSchedulerReset());
              dispatch(reset());
              setEditorKey((prev) => prev + 1); // Force re-render of Editor to reset content
            }}
            disabled={!hasChanges}
          >
            Discard Changes
          </Button>
          <Button
            mt="xs"
            onClick={handleSave}
            disabled={!hasChanges || isSavingEmailTemplate || isSending}
            rightSection={isSavingEmailTemplate ? <Loader size={14} /> : null}
          >
            Save Email Template
          </Button>
          <Fieldset
            legend="Send Emails"
            disabled={!ventResponse.canUserEdit}
            mt="md"
          >
            <Group>
              <Select
                data={["All Guests", "Accepted Guests", "Undecided Guests"]}
                value={recipientFilterTypeToText(oneTimeRecipientFilterType)}
                onChange={(filterType) => {
                  setOneTimeRecipientFilterType(
                    textToRecipientFilterType(filterType),
                  );
                }}
              />
              <Button
                disabled={hasChanges || isSavingEmailTemplate || isSending}
                rightSection={isSending ? <Loader size={14} /> : null}
                onClick={() => {
                  handleSendEmail();
                }}
              >
                Send Now
              </Button>
            </Group>
            <Text size="sm" c="dimmed" mt="sm">
              (Tip: For more control over which guests receive emails, visit
              this event's
              <LinkAnchorToGuests ventId={ventId} size="sm">
                {" guests "}
              </LinkAnchorToGuests>
              page.)
            </Text>
          </Fieldset>
        </div>
      </AppShell.Main>
    </>
  );
};

export default EmailTemplateTab;
